import { MusicalNote } from "../MusicalNote";
import {
  RootFocusedBassPattern,
  SimpleBassPattern,
  WalkingBassPattern,
} from "./BassPatterns";
import {
  MasterComposer,
  MusicalComposer,
  MusicalContext,
} from "./MasterComposer";
import { BassPattern } from "./PatternInterfaces";
import { PhraseManager } from "./PhraseManager";

/**
 * Bass composer that generates phrase-aware bass lines.
 *
 * Uses the pattern system with root-focused patterns for groove portions
 * and transitional patterns for fills. Emphasizes harmonic foundation
 * with root notes on downbeats and scale-based melodic movement.
 *
 * Musical Approach:
 * - Groove portions: Root emphasis with leading tones and chord tones
 * - Fill portions: Scale-based lines that build back to root
 * - Stateless pattern selection based on phrase seed
 * - Intensity affects note density and rhythmic activity
 */
export class BassComposer implements MusicalComposer {
  // Pattern libraries (immutable)
  private readonly groovePatterns: BassPattern[] = [
    new RootFocusedBassPattern(),
  ];

  private readonly fillPatterns: BassPattern[] = [new SimpleBassPattern()];

  /**
   * Generate bass notes for the next beat based on musical context.
   * Stateless - selects and executes patterns based on current context.
   */
  generateNotesForBeat(context: MusicalContext): MusicalNote[] {
    const { timingInfo } = context;
    const pattern = this.selectPattern(context);

    // Execute pattern directly - patterns now contain actual MIDI notes
    return pattern.execute(timingInfo, context.intensity);
  }

  selectPattern(context: MusicalContext): BassPattern {
    const { timingInfo } = context;
    if (PhraseManager.isInFill(timingInfo)) {
      const patternIndex = timingInfo.phraseSeed % this.fillPatterns.length;
      return this.fillPatterns[patternIndex];
    } else {
      const patternIndex = timingInfo.phraseSeed % this.groovePatterns.length;
      return this.groovePatterns[patternIndex];
    }
  }
}
